import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import AppButton from "../../ui/AppButton";
import { getToken } from "../../config/msalConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URI;

const EVENT_META = {
  start:   { icon: InfoOutlinedIcon,         color: "#3b82f6" },
  info:    { icon: InfoOutlinedIcon,         color: "#64748b" },
  created: { icon: AddCircleOutlineIcon,     color: "#16a34a" },
  updated: { icon: SyncAltIcon,             color: "#2563eb" },
  removed: { icon: ErrorOutlineIcon,         color: "#dc2626" },
  skipped: { icon: WarningAmberOutlinedIcon, color: "#f97316" },
  error:   { icon: ErrorOutlineIcon,         color: "#dc2626" },
};

function LogLine({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.info;
  const Icon = meta.icon;
  let text = event.message || "";
  if (event.type === "created") text = `Created: ${event.name}`;
  if (event.type === "updated") text = `Updated: ${event.name}`;
  if (event.type === "removed") text = `Removed (unlicensed): ${event.name}`;
  if (event.type === "skipped") text = `Skipped: ${event.name}${event.reason ? ` (${event.reason})` : ""}`;

  return (
    <Box display="flex" alignItems="flex-start" gap={0.8} py={0.3}>
      <Icon sx={{ fontSize: 14, color: meta.color, flexShrink: 0, mt: "2px" }} />
      <Typography fontSize={12.5} sx={{ lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  );
}

function AzureSyncDialog({ open, onClose, onDone }) {
  const logEndRef = useRef(null);
  const abortRef = useRef(null);
  const [lines, setLines] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  useEffect(() => {
    if (!open) return;
    setLines([]); setIsDone(false); setSummary(null); setError("");
    setIsRunning(true);

    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND}/api/employees/sync-azure/stream`, {
          headers: { "oidc-token": token },
          signal: controller.signal,
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Sync failed"); }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() ?? "";
          for (const chunk of chunks) {
            for (const line of chunk.split("\n")) {
              if (!line.startsWith("data: ")) continue;
              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === "done") {
                  setSummary(event);
                  setIsDone(true);
                  onDone?.();
                } else {
                  setLines((prev) => [...prev, event]);
                }
              } catch { /* ignore */ }
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Sync failed");
      } finally {
        setIsRunning(false);
      }
    })();

    return () => controller.abort();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  return (
    <Dialog open={open} onClose={isDone ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Import from Azure AD</DialogTitle>

      {isRunning && <LinearProgress />}

      <DialogContent sx={{ pt: 1.5 }}>
        <Box
          sx={{
            bgcolor: "background.default", border: "1px solid", borderColor: "divider",
            borderRadius: 2, p: 1.5, minHeight: 200, maxHeight: 360, overflowY: "auto",
          }}
        >
          {lines.length === 0 && isRunning && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography fontSize={13} color="text.secondary">Connecting to Microsoft Graph…</Typography>
            </Box>
          )}
          {lines.map((event, idx) => <LogLine key={idx} event={event} />)}
          <div ref={logEndRef} />
        </Box>

        {isDone && summary && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography fontSize={13} fontWeight={700} mb={1}>Sync complete</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={`${summary.created} created`} size="small" sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }} />
              <Chip label={`${summary.updated} updated`} size="small" sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600 }} />
              <Chip label={`${summary.managers_linked} managers linked`} size="small" sx={{ bgcolor: "#f5f3ff", color: "#7c3aed", fontWeight: 600 }} />
              {summary.promoted > 0 && (
                <Chip label={`${summary.promoted} promoted to manager`} size="small" sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 600 }} />
              )}
              {summary.skipped > 0 && (
                <Chip label={`${summary.skipped} skipped`} size="small" sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600 }} />
              )}
              {summary.removed > 0 && (
                <Chip label={`${summary.removed} removed (unlicensed)`} size="small" sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 600 }} />
              )}
            </Box>
          </>
        )}

        {error && <Typography fontSize={13} color="error" mt={1.5}>{error}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="contained" onClick={handleClose} disabled={isRunning && !isDone}>
          {isDone ? "Close" : "Cancel"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default AzureSyncDialog;
